import { getKeycloakUserManager } from '@/auth/keycloakUserManager'

/**
 * State round-tripped through the Keycloak authorization-code redirect
 * (`signinRedirect` → `user.state` on the `/auth/callback` return).
 */
export interface SsoRedirectState {
  flow: 'sso'
  idpHint: string
  returnUrl?: string
}

export function isSsoRedirectState(s: unknown): s is SsoRedirectState {
  return (
    typeof s === 'object' &&
    s !== null &&
    (s as { flow?: unknown }).flow === 'sso' &&
    typeof (s as { idpHint?: unknown }).idpHint === 'string'
  )
}

/**
 * Anti open-redirect guard: only allow an internal relative path ('/x'). Reject
 * absolute URLs and protocol-relative ('//host') paths.
 */
export function sanitizeReturnUrl(
  raw: string | null | undefined
): string | undefined {
  if (typeof raw !== 'string' || raw === '') return undefined
  // Browsers (and the WHATWG URL parser) normalize backslashes to forward
  // slashes before resolving a URL, so '/\evil.com' would turn into the
  // protocol-relative '//evil.com' — a cross-origin open redirect. Reject
  // any backslash outright, before the slash-prefix checks below.
  if (raw.includes('\\')) return undefined
  if (!raw.startsWith('/') || raw.startsWith('//')) return undefined
  return raw
}

/**
 * Anti open-redirect guard for a network's dedicated domain.
 *
 * Deliberately separate from `sanitizeReturnUrl`, which only ever allows an internal relative
 * path: this value is cross-origin by nature — that is the whole point of a dedicated space —
 * so the guard has to be about *shape* instead. Only `https://host` is accepted, with no
 * path, query, fragment, port, credentials or trailing content.
 *
 * The value comes from `network_domain.host`, written by the provisioning flow rather than by
 * a user, so this is defence in depth. It is worth having anyway: sending a browser to an
 * API-supplied URL is exactly the shape of an open redirect, and the check costs nothing.
 */
export function sanitizeDedicatedDomainUrl(
  raw: string | null | undefined
): string | undefined {
  if (typeof raw !== 'string' || raw === '') return undefined

  let url: URL
  try {
    url = new URL(raw)
  } catch {
    return undefined
  }

  if (url.protocol !== 'https:') return undefined
  if (url.username !== '' || url.password !== '' || url.port !== '')
    return undefined
  if (url.pathname !== '/' || url.search !== '' || url.hash !== '')
    return undefined
  // A hostname is letters, digits, hyphens and dots. Anything else is not a host we minted.
  if (
    !/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/.test(
      url.hostname
    )
  ) {
    return undefined
  }

  return `https://${url.hostname}`
}

/** Triggers the Keycloak authorization-code redirect brokered to the given IdP alias. */
export async function beginSsoRedirect(args: {
  idpHint: string
  returnUrl?: string
  loginHint?: string
}): Promise<void> {
  const state: SsoRedirectState = {
    flow: 'sso',
    idpHint: args.idpHint,
    returnUrl: sanitizeReturnUrl(args.returnUrl),
  }

  await getKeycloakUserManager().signinRedirect({
    extraQueryParams: { kc_idp_hint: args.idpHint },
    // Only set login_hint when provided — oidc-client-ts forwards it verbatim
    // to the authorize request, and an explicit `undefined` value would still
    // add a `login_hint=undefined` query param via URLSearchParams.
    ...(args.loginHint ? { login_hint: args.loginHint } : {}),
    state,
  })
}
